import type {
  OrganizationBillingContext,
  OrganizationBillingTaxProfileResponseData,
} from '@fluenceur/types';
import { isEuMemberCountry } from '@fluenceur/types';
import { Button, Stack, Text } from '@chakra-ui/react';
import { NoticePanel } from '@fluenceur/ui';
import { useTranslation } from 'react-i18next';
import { readBillingTaxProfile } from '@/hooks/queries/use-billing-tax-profile-query';
import {
  canAttemptBillingVatCheck,
  readBillingVatVerification,
  useBillingVatVerificationQuery,
  useBillingVatProfileScope,
} from '@/hooks/queries/use-billing-vat-verification-query';
import { useCheckBillingVatVerification } from '@/hooks/actions/use-check-billing-vat-verification';

const statusCopy = {
  not_required: 'billing.vatVerification.not_required',
  not_checked: 'billing.vatVerification.not_checked',
  invalid_format: 'billing.vatVerification.invalid_format',
  valid: 'billing.vatVerification.valid',
  invalid: 'billing.vatVerification.invalid',
  unavailable: 'billing.vatVerification.unavailable',
} as const;

export function BillingVatVerificationStatus({
  context,
  snapshot,
  draftChanged = false,
  unavailable = false,
}: {
  context: OrganizationBillingContext;
  snapshot: OrganizationBillingTaxProfileResponseData;
  draftChanged?: boolean;
  unavailable?: boolean;
}) {
  const { t } = useTranslation();
  const saved = readBillingTaxProfile(snapshot);
  const { scope } = useBillingVatProfileScope(saved?.profile?.id);
  const query = useBillingVatVerificationQuery({ context, profile: saved });
  const parsed = readBillingVatVerification(query.data);
  const known =
    parsed?.organizationId === scope.organizationId &&
    parsed.brandId === scope.brandId &&
    parsed.profileId === scope.profileId
      ? parsed
      : undefined;
  const check = useCheckBillingVatVerification({ context, profile: saved, verification: known });
  if (!saved?.profile)
    return (
      <Text color="fg.muted" fontSize="sm">
        {t('billing.vatVerification.saveFirst')}
      </Text>
    );
  if (query.isPending && !query.data) return <Text>{t('common.loading')}</Text>;
  if (query.error || !known)
    return (
      <NoticePanel
        tone="warning"
        title={t('billing.vatVerification.readError')}
        actions={
          <Button type="button" variant="outline" onClick={() => void query.refetch()}>
            {t('common.retry')}
          </Button>
        }
      />
    );
  const canCheck =
    canAttemptBillingVatCheck(known) &&
    saved.canManage &&
    context.capabilities.canCommitPaidPlan &&
    context.cardSetup.exemption !== 'impersonation';
  const busy = query.isFetching || check.isPending;
  const tone =
    known.status === 'valid'
      ? 'success'
      : known.status === 'invalid' ||
          known.status === 'unavailable' ||
          known.status === 'invalid_format'
        ? 'warning'
        : 'info';
  return (
    <NoticePanel
      tone={tone}
      title={t('billing.vatVerification.title')}
      actions={
        canCheck ? (
          <Button
            type="button"
            variant="outline"
            loading={check.isPending}
            disabled={busy || unavailable || draftChanged}
            onClick={() => {
              if (busy || unavailable || draftChanged) return;
              void check
                .mutateAsync({
                  expectedProfileId: saved.profile!.id,
                  expectedConsultationId: known.consultation?.id ?? null,
                })
                .catch(() => undefined);
            }}
          >
            {t(
              known.status === 'valid'
                ? 'billing.vatVerification.recheck'
                : known.status === 'invalid' || known.status === 'unavailable'
                  ? 'billing.vatVerification.retry'
                  : 'billing.vatVerification.verify'
            )}
          </Button>
        ) : undefined
      }
    >
      <Stack gap={2}>
        <Text>{t(statusCopy[known.status])}</Text>
        {known.status !== 'valid' &&
          (saved.profile.country === 'GB' ||
            (saved.profile.country !== 'FR' && isEuMemberCountry(saved.profile.country))) && (
            <Text>{t('billing.vatVerification.fallback')}</Text>
          )}
        <Text fontSize="sm" color="fg.muted">
          {t('billing.vatVerification.scope')}
        </Text>
        {draftChanged && <Text fontSize="sm">{t('billing.vatVerification.draft')}</Text>}
      </Stack>
    </NoticePanel>
  );
}
